import { ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { reportError } from '@/lib/errorReporter';
import { authTrail } from '@/lib/authTrail';

// Must match OnboardingWizard's ONBOARDING_KEY — was 'onboarding_v1_done'
// and broke the localStorage fallback when the wizard was bumped to v2,
// so /cctv-design kept bouncing back to /dashboard during the race between
// wizard completion and the user_preferences write round-trip.
const ONBOARDING_KEY = 'onboarding_v2_done';

/** How often a page shown to a person who now reads as signed out looks
 *  again at the stored session. */
export const SIGNED_OUT_GRACE_MS = 4000;

interface ProtectedRouteProps {
  children: ReactNode;
}

type SessionCheck = 'idle' | 'checking' | 'gone';

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { preferences, loading: prefsLoading } = useUserPreferences();
  const location = useLocation();

  // Once the page has been shown to a signed-in person it stays mounted
  // through a later reload of the session or the preferences. Swapping it
  // for the spinner, or bouncing to /auth and straight back, unmounted the
  // planner and lost an unsaved first project (designer-remount, 15.10).
  const shownFor = useRef<string | null>(null);
  const reported = useRef(false);
  const [check, setCheck] = useState<SessionCheck>('idle');
  const keep = shownFor.current !== null && (!user || user.id === shownFor.current);
  const ready = !!user && !authLoading && !prefsLoading;

  useEffect(() => {
    if (ready && user) shownFor.current = user.id;
  }, [ready, user]);

  // Read as signed out after having been shown: believe it only when the
  // stored session is gone too. /auth trusts the stored session, so sending
  // the person there while it lasts only brings them straight back to a
  // remounted planner (a phone, twice in two minutes, 16.10). A failed read
  // is not a missing session: look again a little later.
  useEffect(() => {
    if (user || authLoading || !keep) { setCheck('idle'); return; }
    let cancelled = false;
    let timer = 0;
    const look = () => {
      void supabase.auth.getSession()
        .then(({ data, error }) => {
          if (cancelled) return;
          if (!data.session && !error) { setCheck('gone'); return; }
          // Read as signed out with the session still stored: kept, and told
          // once with what happened just before, to find what drops the user.
          if (data.session && !reported.current) {
            reported.current = true;
            void reportError(new Error('signed out while the session is stored'), {
              component: 'auth-null-with-session',
              trail: authTrail(),
              sameUser: data.session.user?.id === shownFor.current,
              visibility: document.visibilityState,
            });
          }
          timer = window.setTimeout(look, SIGNED_OUT_GRACE_MS);
        })
        .catch(() => { if (!cancelled) timer = window.setTimeout(look, SIGNED_OUT_GRACE_MS); });
    };
    setCheck('checking');
    look();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [user, authLoading, keep]);

  if ((authLoading || prefsLoading) && !keep) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    if (keep && check !== 'gone') return <>{children}</>;
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  // Onboarding is mandatory — redirect to dashboard if not completed
  // Skip this check when already on /dashboard (to avoid infinite redirect),
  // and while the preferences of a page already shown are reloading.
  const isDashboard = location.pathname === '/dashboard' || location.pathname.endsWith('/dashboard');
  const onboardingDone = preferences.onboardingCompleted || !!localStorage.getItem(ONBOARDING_KEY);
  if (!isDashboard && !onboardingDone && !(keep && prefsLoading)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
